import type {ReactElement, ReactNode} from 'react';
import React, {useRef, useEffect} from 'react';
import {PosterNavOpts} from '@Components/poster-editor/components/poster-nav-opts';
import {PosterEditorNavBar} from '@Components/poster-editor/components/poster-editor-nav-bar';
import type {PosterOpts} from '@PosterWhiteboard/poster/poster.class';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {SaveReminderNotification} from '@Components/save-reminder-notification';
import type {PosterEditorConfig} from '@Components/poster-editor/poster-editor.types';
import {MobilePageSelectionModeState} from '@Components/poster-editor/poster-editor.types';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {PosterWhiteboard} from '@Components/poster-whiteboard';
import {PosterEditorBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar';
import {PostPosterModalSpacer} from '@Components/poster-editor/components/post-poster-modal-spacer';
import {usePosterEditorPrefetchModules} from '@Hooks/poster-editor/usePosterEditorPrefetchModules';
import {CanvasZoomPinchOnboarding} from '@Components/poster-whiteboard/components/canvas-zoom-pinch-onboarding';
import {PosterEditingNavbarMode} from '@Components/poster-editor/components/poster-editor-nav-bar/poster-editor-nav-bar.types';
import {PosterEditorErrorBoundary} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editor-error-boundary';
import styles from './poster-editor.module.scss';
import {PosterAddItemBar} from './components/poster-add-item-bar';
import {PosterEditingSidePanel} from './components/poster-editing-side-panel';
import {useAppSelector} from '@/hooks';
// needed in download-publish-dialog.js.
import '@ThirdParty/modernizr.custom.24530';
import '@Global/download-publish-dialog';
import '@Global/message-growl';
// needed in download-publish-dialog.js to open download modal.
import '@Global/modal';
// needed in download-publish-dialog.js.
import '@Global/loading-toast';
// needed in feedback toast
import '@Global/toast';
import {CursorOverlay} from '@Components/poster-editor/components/cursor-overlay';
import {InitalPosterItemsLoadErrorNotification} from '../inital-poster-items-load-error/inital-poster-items-load-error-notification';
import {TimelineMobileOnboarding} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/timeline-mobile-onboarding/timeline-mobile-onboarding';
import {useInitPosterEditor} from '@Hooks/poster-editor/useInitPosterEditor';
import {usePosterPushModalEffects} from '@Hooks/poster-editor/usePosterPushModalEffects';
import {useToggleNotificationOnAISubtitlesGenerating} from '@Hooks/poster-editor/useToggleNotificationOnAISubtitlesGenerating';
import {useEditorMobileOnboarding} from '@Hooks/poster-editor/useEditorMobileOnboarding';
import {useHandleEditorSidebarCollapsibleState} from '@Hooks/poster-editor/useHandleEditorSidebarCollapsibleState';
import {useVersionHistoryShortcuts} from '@Hooks/poster-editor/useVersionHistoryShortcuts';
import {useQRFeatureDemoPreselection} from '@Hooks/poster-editor/useQRFeatureDemoPreselection';
import {PosterEditorLoaders} from '@Components/poster-editor/components/poster-editor-loaders/poster-editor-loaders';
import {PosterEditorWebBottomBarWrapper} from '@Components/poster-editor/components/poster-editor-web-bottom-bar-wrapper/poster-editor-web-bottom-bar-wrapper';
import {useIsCurrentPageVideo} from '@Hooks/poster-editor/useIsCurrentPageVideo';
import {useRestoreOngoingDownloads} from '@Hooks/download-dialog/useRestoreOngoingDownloads';
import {usePremiumDownloads} from '@Hooks/download-dialog/usePremiumDownloads';
import {usePaygDownloads} from '@Hooks/download-dialog/usePaygDownloads';
import {useDownloadProgressAnimation} from '@Hooks/download-dialog/useDownloadProgressAnimation';
import {PostPosterElement} from '@Components/poster-editor/components/post-poster-element';
import {PosterMobileSmallSeekbar} from '@Components/poster-editor/components/poster-mobile-small-seekbar';
import {PosterEditorMobileTimeline} from '@Components/poster-editor/components/poster-editor-mobile-timeline/poster-editor-mobile-timeline';
import {useGenerateDownloadFromDownloadPage} from '@Hooks/download-dialog/useGenerateDownloadFromDownloadPage';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {MobilePageSelector} from '@Components/poster-editor/components/mobile-page-selector/mobile-page-selector';
import {PagesPreviewThumbManager} from '@Components/poster-editor/components/pages-preview-thumb-manager/pages-preview-thumb-manager';
import {usePerformanceStats} from '@Hooks/usePerformanceStats';

window.isPosterMaker = true;
window.posterEditor = {
  whiteboard: undefined,
  config: undefined,
  elements: {},
};

export interface PosterEditorProps {
  className?: string;
  posterOpts: PosterOpts;
  posterEditorConfig: PosterEditorConfig;
  isEmbedded?: boolean;
}

export function PosterEditor({className = '', isEmbedded = false, ...props}: PosterEditorProps): ReactElement | null {
  const isMobileVariant =
    useAppSelector((state) => {
      return state.posterEditor.isMobileVariant;
    }) ?? false;
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });
  const mobilePageSelectionModeState = useAppSelector((state) => {
    return state.posterEditor.mobilePageSelectionModeState;
  });
  const editorWithoutNavRef = useRef<HTMLDivElement>(null);

  const isCurrentPageVideo = useIsCurrentPageVideo();

  usePerformanceStats();
  usePosterEditorPrefetchModules();
  useInitPosterEditor({posterEditorConfig: props.posterEditorConfig});
  usePosterPushModalEffects();
  useToggleNotificationOnAISubtitlesGenerating();
  useEditorMobileOnboarding({
    showOnboardingFromConfig: props.posterEditorConfig.showMobileUXOnBoarding,
  });
  useHandleEditorSidebarCollapsibleState();
  useVersionHistoryShortcuts();
  useQRFeatureDemoPreselection();
  usePremiumDownloads();
  usePaygDownloads();
  useGenerateDownloadFromDownloadPage();
  useRestoreOngoingDownloads(window.posterEditor?.whiteboard?.id, window.posterEditor?.whiteboard !== undefined);
  useDownloadProgressAnimation();

  useEffect(() => {
    if (editorWithoutNavRef.current && window.posterEditor) {
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.EDITOR_WITHOUT_NAV] = $(editorWithoutNavRef.current);
    }
  }, []);

  useEffect(() => {
    if (isTimelineModeActive && !isCurrentPageVideo) {
      window.posterEditor?.whiteboard?.updateIsTimelineMode(false);
    }
  }, [isTimelineModeActive, isCurrentPageVideo]);

  const getNavBar = (): ReactElement => {
    const mode = window.posterEditor?.whiteboard?.mode;
    if (isVersionHistoryModeActive) {
      return <PosterEditorNavBar mode={PosterEditingNavbarMode.VERSION_HISTORY} />;
    }
    if (isEmbedded) {
      return <PosterEditorNavBar mode={PosterEditingNavbarMode.EMBEDDED} />;
    }
    switch (mode?.details.type) {
      case PosterModeType.REGEN:
        return <PosterEditorNavBar mode={PosterEditingNavbarMode.REGEN} />;
      default:
        return <PosterEditorNavBar />;
    }
  };

  const getAddItemBar = (): ReactElement => {
    return <PosterAddItemBar />;
  };

  const getEditorContainerClasses = (): string => {
    return [styles.editorWithoutNav, isMobileVariant ? styles.editorMobile : styles.dottedBg, isMobileVariant && isTimelineModeActive ? styles.mobileTimelineActive : '']
      .filter(Boolean)
      .join(' ');
  };

  const getPosterWhiteboard = (): ReactElement => {
    return (
      <PosterEditorErrorBoundary>
        <PosterWhiteboard isMobileDisplay={isEditorMobileVariant()} isCenterAligned posterOpts={props.posterOpts} />
      </PosterEditorErrorBoundary>
    );
  };

  const getPosterEditorLoaders = (): ReactElement | null => {
    return !isVersionHistoryModeActive ? <PosterEditorLoaders /> : null;
  };

  const getPageSelector = (): ReactNode => {
    if (mobilePageSelectionModeState === MobilePageSelectionModeState.INACTIVE) {
      return null;
    }

    return <MobilePageSelector className={`_position-absolute _full-width`} />;
  };

  const getMobileVariant = (): ReactElement => {
    return (
      <>
        <CursorOverlay />
        <div className={`_full-width _full-height _position-relative`}>
          {getPageSelector()}
          <PosterWhiteboard
            className={`_full-height _position-absolute ${mobilePageSelectionModeState === MobilePageSelectionModeState.ACTIVE ? styles.hidden : ''}`}
            isMobileDisplay
            postWhiteboardElement={<PostPosterElement />}
            isCenterAligned
            posterOpts={props.posterOpts}
          />
        </div>
        <PosterMobileSmallSeekbar />
        {getPosterEditorLoaders()}
        <React.StrictMode>
          {isTimelineModeActive ? <PosterEditorMobileTimeline /> : null}
          <PostPosterModalSpacer />
          <PosterEditorBottomBar />
        </React.StrictMode>
      </>
    );
  };

  const getWebVariant = (): ReactElement => {
    return (
      <>
        <div className={`flex flex-column-align-center _full-height _full-width ${!isSidebarSmall ? styles.maxWidth : ''}`}>
          <div className={`flex flex-row-align-center _full-width ${styles.transitionAccordingToTimeline} _full-height`}>
            <React.StrictMode>{getAddItemBar()}</React.StrictMode>
            <CursorOverlay />
            {getPosterWhiteboard()}
            {getPosterEditorLoaders()}
          </div>
          <React.StrictMode>
            <PosterEditorWebBottomBarWrapper />
          </React.StrictMode>
        </div>
        <React.StrictMode>
          <PosterEditingSidePanel />
        </React.StrictMode>
      </>
    );
  };

  return (
    <>
      <PagesPreviewThumbManager />

      <div className={`${styles.editor} ${className} ${isMobileVariant ? 'poster-editor-mobile-variant' : 'poster-editor-web-variant'}`}>
        <React.StrictMode>{getNavBar()}</React.StrictMode>
        <div ref={editorWithoutNavRef} className={getEditorContainerClasses()}>
          {isMobileVariant ? getMobileVariant() : getWebVariant()}
        </div>
        <React.StrictMode>
          {isMobileVariant ? null : <PosterNavOpts />}
          {!isMobileVariant ? <SaveReminderNotification /> : null}
          <InitalPosterItemsLoadErrorNotification />
        </React.StrictMode>
      </div>
      {isMobileVariant ? <CanvasZoomPinchOnboarding /> : null}
      {isMobileVariant ? <TimelineMobileOnboarding /> : null}
    </>
  );
}
